import { AxiosConfigExplore } from "Explore/Config/AxiosConfig";

/**
 * PowerPoint (.pptx) benchmarking report export.
 *
 * Two-step, template-driven flow (see export_ppt.md):
 *   1. `getReportAnchors` — read the template's placeholder codes and the exact
 *      pixel size each chart must be rendered at (never hardcode these).
 *   2. `exportBenchmarkReport` — upload the rendered chart PNGs (one multipart
 *      part per anchor code) plus a JSON payload; receive the .pptx blob.
 *
 * Uses the Explore axios client (auto-attaches the Bearer token; the browser
 * adds the multipart boundary and the Origin header).
 */

export const REPORT_TEMPLATE = "academic_explore_research_report";

const REPORT_BASE = "/benchmarking/report";

// ─── Template placeholder codes (defined in the backend template) ────────────
// Matched case-insensitively. Charts are uploaded as image file parts; tables
// as `blocks` entries (native, editable PowerPoint tables). Single-value metric
// placeholders are filled by the backend from keywords + institution_ids.

// Codes captured from an on-screen element (charts + the breakdown table which
// is a live DOM table) → our reportable id (+ legacy aliases).
export const PPTX_CHART_CODE_TO_REPORT_ID = {
  METRIC_SCALING_CHART: "metric-scaling",
  BUBBLE_OUTPUT: "output-vs-impact",
  HEAT_MAP: "heatmap",
  CITATION_OVER_TIME: "citations-over-time",
  PUBLICATION_OVER_TIME: "publications-over-time",
  GRANT_OVER_TIME: "grants-over-time",
  BREAKDOWN_TABLE: "breakdown",
  // legacy aliases (older template naming)
  BUBBLE__OUTPUT_CHART: "output-vs-impact",
  BUBBLE_OUTPUT_CHART: "output-vs-impact",
  CITATION_OVERTIME_CHART: "citations-over-time",
};

// Codes rendered from computed data into an image table (the PDF explanation
// tables) → our reportable id.
export const PPTX_TABLE_CODE_TO_REPORT_ID = {
  METRIC_SCALING_TABLE: "metric-scaling",
  BUBBLE_OUTPUT_TABLE: "output-vs-impact",
  CITATION_OVER_TIME_TABLE: "citations-over-time",
  PUBLICATION_OVER_TIME_TABLE: "publications-over-time",
  GRANT_OVER_TIME_TABLE: "grants-over-time",
};

// Per-keyword top-faculty cards: one image part TOP_FACULTY_{n} (1-based) per
// picked keyword. Selecting the "top-faculty" report item drives these.
export const PPTX_TOP_FACULTY_REPORT_ID = "top-faculty";
export const pptxTopFacultyCode = (n) => `TOP_FACULTY_${n}`;

// Metric sections → slot-based PPTX placeholders. Keyed by the metric section
// id (matches BENCHMARK_METRIC_SECTIONS[].id). In each section the user picks
// up to `slots` metric charts; slot i (1-based) uploads the chart image as
// `${prefix}_${i}` (FormData file part) and the metric's display name as
// `${prefix}_${i}_NAME` (payload.blocks text). The section report id is
// `section-${id}`.
export const PPTX_METRIC_SECTIONS = {
  impact: { prefix: "CITATION_IMPACT", slots: 4 },
  capacity: { prefix: "CAPACITY_AND_PRODUCTIVITY", slots: 2 },
  momentum: { prefix: "MOMENTUM_REACH", slots: 2 },
};

export const pptxSectionImageCode = (prefix, n) => `${prefix}_${n}`;
export const pptxSectionNameCode = (prefix, n) => `${prefix}_${n}_NAME`;

/** All slot-based section image codes, e.g. CITATION_IMPACT_1..4. */
export const PPTX_SECTION_IMAGE_CODES = Object.values(
  PPTX_METRIC_SECTIONS
).flatMap(({ prefix, slots }) =>
  Array.from({ length: slots }, (_, i) => pptxSectionImageCode(prefix, i + 1))
);

/**
 * Fallback chart anchor sizes, used only if the live `/anchors` endpoint is
 * unavailable. Real sizes from `/anchors` override these when present.
 */
// Fallback sizes match the current template geometry (all IMAGE placeholders
// are 3011×1418). Real sizes from /anchors override these when present.
export const DEFAULT_PPTX_ANCHORS = {
  METRIC_SCALING_CHART: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  BUBBLE_OUTPUT: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  HEAT_MAP: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  CITATION_OVER_TIME: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  PUBLICATION_OVER_TIME: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  GRANT_OVER_TIME: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  METRIC_SCALING_TABLE: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  BUBBLE_OUTPUT_TABLE: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  CITATION_OVER_TIME_TABLE: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  PUBLICATION_OVER_TIME_TABLE: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  GRANT_OVER_TIME_TABLE: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  BREAKDOWN_TABLE: { type: "IMAGE", width_px: 3011, height_px: 1418 },
  TOP_FACULTY_1: { type: "IMAGE", width_px: 1400 },
  TOP_FACULTY_2: { type: "IMAGE", width_px: 1400 },
  // Metric-section chart slots (one bar chart per user-chosen metric). Captured
  // box:true, so a fallback height_px is needed when /anchors is unavailable.
  ...PPTX_SECTION_IMAGE_CODES.reduce((acc, code) => {
    acc[code] = { type: "IMAGE", width_px: 1400, height_px: 900 };
    return acc;
  }, {}),
};

/** Placeholder anchors + per-chart target pixel sizes for a template. */
export const getReportAnchors = async (
  template = REPORT_TEMPLATE,
  dpi = 300
) => {
  const axios = AxiosConfigExplore();
  const res = await axios.get(`${REPORT_BASE}/templates/${template}/anchors`, {
    params: { dpi },
  });
  return res.data?.data ?? null;
};

/**
 * Render the deck. Multipart parts:
 *   - `payload` — JSON body (template, keywords, ids, title).
 *   - `files`   — placeholder code → PNG Blob. Both charts AND tables are
 *                 uploaded here as image parts (the backend drops each straight
 *                 into its placeholder).
 * Returns the .pptx as a Blob.
 */
export const exportBenchmarkReport = async ({
  payload,
  files = {},
  time_filter = "all",
}) => {
  const axios = AxiosConfigExplore();
  const form = new FormData();
  form.append("payload", JSON.stringify(payload));
  Object.entries(files).forEach(([code, blob]) => {
    if (blob) form.append(code, blob, `${code}.png`);
  });
  const res = await axios.post(`${REPORT_BASE}/export`, form, {
    params: { time_filter },
    responseType: "blob",
    // Backend enforces a 30s cap (408); give the client a little more headroom.
    timeout: 60000,
  });
  return res.data;
};
